// ── BCH Live — Monetization Panels ────────────────────────────────
//
// The BCH-native surfaces that sit around the player. These are what
// make BCH Live different from a generic livestream client, so they stay
// front and centre rather than buried behind a menu:
//
//   • SupportersBar      — total raised + top supporters rail
//   • SuperChatFeed      — dedicated live donation feed
//   • DonationGoalCard   — creator goal with progress
//   • TipBurst           — coin animation when BCH lands
//   • QuickTipRow        — one-click preset tipping
//   • CrossPromoRow      — crowdfunding + marketplace integration

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Coins, Trophy, TrendingUp, Target, Heart, Sparkles, User,
  Rocket, Store, ArrowRight, Loader2, Check,
} from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import { useNostrProfile } from '../../hooks/useNostrQuery';
import { useNostrPublish } from '../../hooks/useNostrPublish';
import { useTipWallet } from '../../hooks/useTipWallet';
import { prefetchBCHAddress, getCachedBCHAddress } from '../../hooks/useBCHAddress';
import { getBCHPrice } from '../../hooks/useBCHPrice';
import { timeAgo } from '../../lib/utils';
import { slog, swarn } from '../../lib/streamCredentials';
import { TIP_RECEIPT_KIND, type StreamGoal } from '../../hooks/useStreamTips';
import { formatUsd, tierColor, type TipEntry } from './StreamOverlays';

// ══════════════════════════════════════════════════════════════════
//  SHARED BITS
// ══════════════════════════════════════════════════════════════════

function Avatar({ pubkey, size = 20 }: { pubkey: string; size?: number }) {
  const { profile } = useNostrProfile(pubkey);
  const style = { width: size, height: size };
  return profile?.picture ? (
    <img src={profile.picture} alt="" loading="lazy" style={style} className="rounded-full object-cover" />
  ) : (
    <div style={style} className="flex items-center justify-center rounded-full bg-primary/10">
      <User className="h-3 w-3 text-primary/60" />
    </div>
  );
}

function Name({ pubkey }: { pubkey: string }) {
  const { profile } = useNostrProfile(pubkey);
  return <>{profile?.display_name || profile?.name || pubkey.slice(0, 8)}</>;
}

// ══════════════════════════════════════════════════════════════════
//  SUPPORTERS BAR
// ══════════════════════════════════════════════════════════════════

export const SupportersBar = React.memo(function SupportersBar({
  supporters, totalUsd,
}: {
  supporters: Array<{ pubkey: string; usd: number; count: number }>;
  totalUsd: number;
}) {
  if (supporters.length === 0) return null;
  const top = supporters.slice(0, 6);

  return (
    <div className="live-rail flex items-center gap-3 overflow-x-auto py-1">
      <div className="flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1.5">
        <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
        <span className="text-[11px] font-extrabold text-emerald-600 dark:text-emerald-400">
          {formatUsd(totalUsd)} raised
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        <Trophy className="h-3.5 w-3.5 shrink-0 text-amber-500" />
        {top.map((s, i) => (
          <Link
            key={s.pubkey}
            to={`/profile/${s.pubkey}`}
            title={`${formatUsd(s.usd)} across ${s.count} tip${s.count === 1 ? '' : 's'}`}
            className="flex items-center gap-1 rounded-full bg-muted px-2 py-1 transition-colors hover:bg-accent"
          >
            {i === 0 && <span className="text-[10px]">👑</span>}
            <Avatar pubkey={s.pubkey} size={18} />
            <span className="text-[10px] font-extrabold" style={{ color: tierColor(s.usd) }}>
              {formatUsd(s.usd)}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
});

// ══════════════════════════════════════════════════════════════════
//  SUPER CHAT FEED
// ══════════════════════════════════════════════════════════════════

const SuperChatCard = React.memo(function SuperChatCard({
  tip, isNew,
}: { tip: TipEntry; isNew?: boolean }) {
  const color = tierColor(tip.usd);
  const isBig = tip.usd >= 5;

  return (
    <div
      className={`rounded-xl border-2 p-3 ${
        isNew ? (isBig ? 'sc-enter sc-glow-big' : 'sc-enter sc-glow') : ''
      }`}
      style={{ borderColor: color, backgroundColor: `${color}10` }}
    >
      <div className="flex items-start gap-2.5">
        <Link to={`/profile/${tip.pubkey}`} className="shrink-0">
          <Avatar pubkey={tip.pubkey} size={isBig ? 28 : 22} />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <Link
              to={`/profile/${tip.pubkey}`}
              className={`max-w-[110px] truncate font-bold hover:opacity-80 ${isBig ? 'text-sm' : 'text-xs'}`}
              style={{ color }}
            >
              <Name pubkey={tip.pubkey} />
            </Link>
            <span
              className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 font-black text-white"
              style={{ backgroundColor: color, fontSize: isBig ? 12 : 10 }}
            >
              <Coins className={isBig ? 'h-3.5 w-3.5' : 'h-3 w-3'} />
              {formatUsd(tip.usd)}
            </span>
          </div>
          {tip.message && (
            <p className={`mt-1 whitespace-pre-wrap break-words text-foreground/90 ${isBig ? 'text-sm' : 'text-xs'}`}>
              {tip.message}
            </p>
          )}
        </div>
        <span className="shrink-0 text-[9px] text-muted-foreground">{timeAgo(tip.created_at)}</span>
      </div>
    </div>
  );
});

export const SuperChatFeed = React.memo(function SuperChatFeed({
  tips, newIds, onSuperChat, canSuperChat,
}: {
  tips: TipEntry[];
  newIds: Set<string>;
  onSuperChat?: () => void;
  canSuperChat?: boolean;
}) {
  if (tips.length === 0) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 text-center">
        <div className="mx-auto mb-2.5 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/10">
          <Heart className="h-6 w-6 text-emerald-500/40" />
        </div>
        <p className="text-xs font-bold">No Super Chats yet</p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Be the first to send Bitcoin Cash.
        </p>
        {canSuperChat && onSuperChat && (
          <button
            onClick={onSuperChat}
            className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-emerald-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-emerald-600"
          >
            <Sparkles className="h-3.5 w-3.5" /> Send one
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex max-h-[420px] flex-col overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex shrink-0 items-center justify-between border-b border-border bg-muted/30 px-4 py-2.5">
        <h3 className="flex items-center gap-2 text-sm font-bold">
          <Sparkles className="h-4 w-4 text-emerald-500" /> Super Chats
        </h3>
        <span className="text-[10px] font-semibold text-muted-foreground">{tips.length}</span>
      </div>
      <div className="live-chat-scroll min-h-0 flex-1 space-y-2 overflow-y-auto p-2">
        {tips.map(t => (
          <SuperChatCard key={t.id} tip={t} isNew={newIds.has(t.id)} />
        ))}
      </div>
    </div>
  );
});

// ══════════════════════════════════════════════════════════════════
//  DONATION GOAL
// ══════════════════════════════════════════════════════════════════

export const DonationGoalCard = React.memo(function DonationGoalCard({
  goal, onSupport,
}: { goal: StreamGoal; onSupport?: () => void }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-2">
        <Target className="h-4 w-4 shrink-0 text-emerald-500" />
        <h3 className="min-w-0 flex-1 truncate text-sm font-bold">{goal.title}</h3>
        {goal.complete && (
          <span className="shrink-0 rounded-md bg-amber-400 px-1.5 py-0.5 text-[10px] font-black text-amber-950">
            Reached
          </span>
        )}
      </div>

      {goal.description && (
        <p className="mt-1.5 line-clamp-2 text-[11px] leading-relaxed text-muted-foreground">
          {goal.description}
        </p>
      )}

      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="text-lg font-extrabold text-emerald-500">{formatUsd(goal.raisedUsd)}</span>
        <span className="text-[11px] font-semibold text-muted-foreground">
          of {formatUsd(goal.targetUsd)}
        </span>
        <span className="ml-auto text-xs font-extrabold tabular-nums text-muted-foreground">
          {goal.pct}%
        </span>
      </div>

      <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full transition-[width] duration-700 ${
            goal.complete
              ? 'bg-gradient-to-r from-amber-400 to-yellow-300'
              : 'bg-gradient-to-r from-emerald-500 to-teal-400'
          }`}
          style={{ width: `${Math.max(2, goal.pct)}%` }}
        />
      </div>

      {onSupport && (
        <button
          onClick={onSupport}
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl bg-emerald-500 py-2.5 text-xs font-bold text-white transition-colors hover:bg-emerald-600"
        >
          <Coins className="h-3.5 w-3.5" /> Chip in with BCH
        </button>
      )}
    </div>
  );
});

// ══════════════════════════════════════════════════════════════════
//  TIP BURST
//
//  A short coin animation fired when a tip lands. Purely decorative and
//  pointer-transparent, so it can never intercept player clicks.
// ══════════════════════════════════════════════════════════════════

export function TipBurst({ trigger }: { trigger: string | null }) {
  const [coins, setCoins] = useState<number[]>([]);

  useEffect(() => {
    if (!trigger) return;
    const batch = Array.from({ length: 6 }, (_, i) => i);
    setCoins(batch);
    const t = setTimeout(() => setCoins([]), 1500);
    return () => clearTimeout(t);
  }, [trigger]);

  if (coins.length === 0) return null;

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-14 z-20 flex justify-center gap-3">
      {coins.map(i => (
        <span
          key={i}
          className="coin-rise text-xl"
          style={{ animationDelay: `${i * 90}ms` }}
        >
          💚
        </span>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  QUICK TIP ROW
//
//  One-click BCH tipping with no modal at all. Presets only — anything
//  more expressive routes into the Super Chat sheet.
// ══════════════════════════════════════════════════════════════════

const QUICK_PRESETS = [0.05, 0.25, 1];

export function QuickTipRow({
  creatorPubkey, aTag, onSent,
}: { creatorPubkey: string; aTag: string; onSent?: () => void }) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const tipWallet = useTipWallet();
  const [busy, setBusy] = useState<number | null>(null);
  const [done, setDone] = useState<number | null>(null);
  const [error, setError] = useState('');

  if (!user || user.pubkey === creatorPubkey) return null;

  const send = async (usd: number) => {
    if (busy !== null) return;
    setError('');
    setBusy(usd);
    try {
      if (!tipWallet.ready) await tipWallet.init();
      const price = getBCHPrice();
      if (!price) throw new Error('Price unavailable');
      const sats = Math.round((usd / price) * 1e8);

      let addr = getCachedBCHAddress(creatorPubkey);
      if (!addr) addr = await prefetchBCHAddress(creatorPubkey);
      if (!addr) throw new Error('No BCH address');

      const txid = await tipWallet.sendTipWithFee(addr, BigInt(sats));
      slog('quicktip', `sent ${formatUsd(usd)} (${sats} sats)`);
      setDone(usd);
      setTimeout(() => setDone(null), 1800);

      // Receipt is fire-and-forget — the BCH already moved.
      publish({
        kind: TIP_RECEIPT_KIND,
        content: JSON.stringify({ amount_sats: sats, amount_usd: usd, txid: txid || '' }),
        tags: [
          ['p', creatorPubkey],
          ['a', aTag],
          ['amount', String(sats)],
          ['t', 'bch-tip'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      }).catch(err => swarn('quicktip', 'receipt publish failed', err));

      onSent?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Tip failed');
      setTimeout(() => setError(''), 3000);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-1.5">
        <span className="mr-0.5 shrink-0 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Quick tip
        </span>
        {QUICK_PRESETS.map(usd => (
          <button
            key={usd}
            onClick={() => send(usd)}
            disabled={busy !== null}
            className="flex items-center gap-1 rounded-lg bg-emerald-500/10 px-2.5 py-1.5 text-[11px] font-extrabold text-emerald-600 transition-colors hover:bg-emerald-500/20 disabled:opacity-50 dark:text-emerald-400"
          >
            {busy === usd ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : done === usd ? (
              <Check className="h-3 w-3" />
            ) : (
              <Coins className="h-3 w-3" />
            )}
            {formatUsd(usd)}
          </button>
        ))}
      </div>
      {error && <p className="mt-1 text-[10px] font-semibold text-red-500">{error}</p>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  CROSS-PROMOTION
//
//  Streams are one part of a creator's BCH economy. These link the
//  stream to the crowdfunding and marketplace surfaces.
// ══════════════════════════════════════════════════════════════════

export function CrossPromoRow({ creatorPubkey }: { creatorPubkey: string }) {
  // NOTE: /crowdfund and /marketplace don't read query params, so these
  // deliberately link to the index rather than fake a per-creator filter.
  const links = useMemo(() => ([
    {
      to: `/support?creator=${creatorPubkey}`,
      icon: Heart,
      label: 'Memberships',
      body: 'Support them monthly',
      accent: 'text-rose-500',
    },
    {
      to: '/crowdfund',
      icon: Rocket,
      label: 'Crowdfunding',
      body: 'Back BCH campaigns',
      accent: 'text-violet-500',
    },
    {
      to: '/marketplace',
      icon: Store,
      label: 'Marketplace',
      body: 'Spend BCH on goods',
      accent: 'text-amber-500',
    },
  ]), [creatorPubkey]);

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      {links.map(l => (
        <Link
          key={l.to}
          to={l.to}
          className="group flex items-center gap-2.5 rounded-xl border border-border bg-card p-3 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
        >
          <l.icon className={`h-4 w-4 shrink-0 ${l.accent}`} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-bold">{l.label}</p>
            <p className="truncate text-[10px] text-muted-foreground">{l.body}</p>
          </div>
          <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </Link>
      ))}
    </div>
  );
}
